import React from 'react';
import { Sequence, AbsoluteFill, useCurrentFrame } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { LayerItem } from './base';
import { AnimationConfig, SoundConfig } from './animated';
import { SequenceTimingHelper } from '../helpers/SequenceTimingHelper';
import { PositionedItem, PositionedItemImpl } from './positioned';
import { GridArea } from './grid';
import { Position, calculateGridPosition } from '../Utils';
import { TextMedia, TextMediaImpl } from '../media/text';
import { AdvancedTextEngine } from '../text/AdvancedTextEngine';
import { 
    TextStyle,
    AdvancedTextConfig, 
    TextPageData, 
    TextToken, 
    DEFAULT_TEXT_STYLE,
    FlexibleTextConfig,
    BasicTextStyle 
} from '../text/types';

export interface CaptionItem extends PositionedItem<TextMedia> {
    text: string;
    tokens?: CaptionToken[];
    style?: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        backgroundColor?: string;
        padding?: number;
        borderRadius?: number;
    };
    // Shared text styling options - used by both TextOverlay and Caption systems
    textStyle?: Partial<TextStyle>;
    // OPTIONAL animation properties - only use when needed
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations?: AnimationConfig[];
    customSoundEffects?: SoundConfig[];
}

export interface CaptionToken {
    text: string;
    fromMs: number;
    toMs: number;
    index?: number;
    verticalOffset?: number;
    style?: {
        fontSize?: number;
        fontWeight?: string;
        color?: string;
        backgroundColor?: string;
        borderRadius?: number;
    };
}

export class CaptionItemImpl extends PositionedItemImpl<TextMedia> implements CaptionItem {
    text: string;
    tokens?: CaptionToken[];
    // OPTIONAL animation properties - only set when specified
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations?: AnimationConfig[];
    customSoundEffects?: SoundConfig[];
    style: {
        fontSize: number;
        fontFamily: string;
        color: string;
        backgroundColor: string;
        padding: number;
        borderRadius: number;
    };
    textStyle?: Partial<TextStyle>;

    constructor(caption: Omit<CaptionItem, 'render' | 'mediaObject'>) {
        // Create a text media object for the caption
        const textMedia = new TextMediaImpl(caption.id + '-text', 'captions', caption.text);
        super({ ...caption, mediaObject: textMedia });
        
        this.text = caption.text;
        this.tokens = caption.tokens;
        
        // Positioning is already resolved by the base class constructor.
        // Do not override with undefined values here to preserve type safety.
        // Only set animation properties if they're provided
        this.entranceAnimation = caption.entranceAnimation;
        this.exitAnimation = caption.exitAnimation;
        this.entranceSoundEffect = caption.entranceSoundEffect;
        this.exitSoundEffect = caption.exitSoundEffect;
        this.customAnimations = caption.customAnimations ?? [];
        this.customSoundEffects = caption.customSoundEffects ?? [];
        this.style = {
            fontSize: caption.style?.fontSize ?? 24,
            fontFamily: caption.style?.fontFamily ?? 'Arial',
            color: caption.style?.color ?? '#ffffff',
            backgroundColor: caption.style?.backgroundColor ?? 'rgba(0, 0, 0, 0.7)',
            padding: caption.style?.padding ?? 10,
            borderRadius: caption.style?.borderRadius ?? 5,
        };
        
        // Initialize shared text style if provided
        this.textStyle = caption.textStyle;
    }

    // Sound effects are handled by the timeline's SoundPlayer component
    // This method is kept for interface compatibility but is not used

    /**
     * Render content without creating a sequence (for use by CaptionLayerContainer)
     */
    renderContent(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        return (
            <AbsoluteFill>
                <AdvancedCaptionContent
                    caption={this}
                    fps={fps}
                    theme={theme}
                    aspectRatio={aspectRatio}
                    defaults={defaults}
                    videoConfig={videoConfig}
                />
            </AbsoluteFill>
        );
    }

    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        const hasAdvancedFeatures = this.tokens || this.textStyle?.textAnimation !== 'none' || this.textStyle?.outline !== 'none';
        const hasAnimations = this.entranceAnimation || this.exitAnimation;
        
        if (hasAdvancedFeatures || hasAnimations) {
            // ADVANCED CAPTION: Use sophisticated caption system
            const { startFrame, durationFrames } = SequenceTimingHelper.calculateSequenceTiming(
                this.startAt,
                this.duration,
                fps,
                this.entranceAnimation,
                this.exitAnimation
            );
            
            // Create descriptive sequence name with text animation info
            let sequenceName = SequenceTimingHelper.createSequenceName(
                'Caption',
                this.id,
                this.startAt,
                this.duration,
                this.entranceAnimation,
                this.exitAnimation
            );
            
            // Add text animation info to the name if present
            if (this.textStyle?.textAnimation !== 'none') {
                sequenceName = sequenceName.replace('[', `[textStyle:${this.textStyle?.textAnimation}|`);
            }
            
            const sequenceProps = {
                key: this.id,
                name: sequenceName,
                from: startFrame,
                durationInFrames: durationFrames
            };

            // Enable via debug flag if needed: 
            // if (process.env.NODE_ENV === 'development' && process.env.DEBUG_CAPTIONS) {
            //     console.log(`[SEQUENCE RENDER DEBUG] ${this.id}: Creating sequence with`, sequenceProps);
            // }
            return (
                <Sequence {...sequenceProps}>
                    <AbsoluteFill>
                        <AdvancedCaptionContent
                            caption={this}
                            fps={fps}
                            theme={theme}
                            aspectRatio={aspectRatio}
                            defaults={defaults}
                            videoConfig={videoConfig}
                        />
                    </AbsoluteFill>
                </Sequence>
            );
        } else {
            // BASIC CAPTION: Use simple, direct rendering like before
            const startFrame = this.startAt * fps;
            const durationFrames = (this.duration || 0) * fps;
            
            // Create descriptive sequence name using centralized helper
            const sequenceName = SequenceTimingHelper.createSequenceName(
                'Caption',
                this.id,
                this.startAt,
                this.duration,
                undefined, // no entrance animation
                undefined, // no exit animation
                true // isBasic flag
            );

            const sequenceProps = {
                key: this.id,
                name: sequenceName,
                from: startFrame,
                durationInFrames: durationFrames
            };

            return (
                <Sequence {...sequenceProps}>
                    <AbsoluteFill>
                        <BasicCaptionContent
                            caption={this}
                            fps={fps}
                            theme={theme}
                            aspectRatio={aspectRatio}
                            defaults={defaults}
                            videoConfig={videoConfig}
                        />
                    </AbsoluteFill>
                </Sequence>
            );
        }
    }
}


/**
 * BasicCaptionContent component handles simple captions with grid positioning
 * Uses the timeloom 5x5 grid system for consistent positioning
 */
const BasicCaptionContent: React.FC<{
    caption: CaptionItemImpl;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ caption, aspectRatio, defaults, videoConfig }) => {
    // Calculate grid position using timeloom's positioning system
    const position = calculateGridPosition(
        caption.gridArea,
        caption.scale,
        caption.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
        caption.paddingMode ?? 'container',
        caption.position ?? Position.Center
    );
    
    return (
        <div
            style={{
                position: 'absolute',
                ...position,
                fontSize: caption.style.fontSize,
                fontFamily: caption.style.fontFamily,
                color: caption.style.color,
                backgroundColor: caption.style.backgroundColor,
                padding: caption.style.padding,
                borderRadius: caption.style.borderRadius,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: caption.opacity
            }}
        >
            {caption.text}
        </div>
    );
};

/**
 * AdvancedCaptionContent component handles sophisticated caption features
 * Uses the professional AdvancedTextEngine for all rendering
 */
const AdvancedCaptionContent: React.FC<{
    caption: CaptionItemImpl;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ caption, fps, aspectRatio, defaults, videoConfig }) => {
    const currentFrame = useCurrentFrame();
    
    // Generate default tokens if none provided
    const tokens = caption.tokens || generateDefaultTokens(caption.text, caption.startAt, caption.duration || 5);
    
    // Use centralized sequence animation styles helper
    const { animationStyles, visibility } = SequenceTimingHelper.getSequenceAnimationStyles(
        currentFrame,
        fps,
        caption.duration,
        caption.entranceAnimation,
        caption.exitAnimation,
        {}, // No base transform for advanced captions
        1 // Let finalContainerStyles handle opacity
    );
    
    // Get text style or use defaults
    const textStyle = caption.textStyle;
    
    // Calculate grid position using timeloom's positioning system
    const position = calculateGridPosition(
        caption.gridArea,
        caption.scale,
        caption.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
        caption.paddingMode ?? 'container',
        caption.position ?? Position.Center
    );
    
    // FIXED: Extract CSS animation classes from animation styles
    // The CSS animation system returns special properties that need to be applied as classes
    const cssAnimationClass = (animationStyles as any).animationClass;
    const cssAnimationPrefix = (animationStyles as any).animationPrefix || 'animate__';
    
    // Create clean CSS properties without the special class properties
    const cleanAnimationStyles = { ...animationStyles };
    delete (cleanAnimationStyles as any).animationClass;
    delete (cleanAnimationStyles as any).animationName;
    delete (cleanAnimationStyles as any).animationPrefix;
    
    const finalContainerStyles = {
        position: 'absolute' as const,
        ...position,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: caption.opacity,
        ...cleanAnimationStyles,
        ...visibility
    };
    
    // Build className for CSS animations
    let className = '';
    if (cssAnimationClass) {
        // Apply the base animate.css class and the specific animation class
        className = `${cssAnimationPrefix}animated ${cssAnimationClass}`;
        console.log(`[CSS_ANIMATION_FIX] Caption ${caption.id}: Applying CSS classes: ${className}`);
    }
    
    // Convert CaptionToken to TextToken for AdvancedTextEngine
    const textTokens: TextToken[] = tokens.map(token => ({
        text: token.text,
        fromMs: token.fromMs,
        toMs: token.toMs,
        index: token.index,
        verticalOffset: token.verticalOffset,
        style: token.style
    }));
    
    // Create page data for AdvancedTextEngine
    const pageData: TextPageData = {
        text: caption.text,
        tokens: textTokens,
        startMs: caption.startAt * 1000
    };
    
    // Convert caption configuration to TextStyle with defaults
    const textStyleConfig: TextStyle = {
        primaryColor: textStyle?.primaryColor ?? DEFAULT_TEXT_STYLE.primaryColor,
        currentSpokenWordColor: textStyle?.currentSpokenWordColor ?? DEFAULT_TEXT_STYLE.currentSpokenWordColor,
        fontFamily: textStyle?.fontFamily ?? caption.style.fontFamily,
        maxFontSize: textStyle?.maxFontSize ?? DEFAULT_TEXT_STYLE.maxFontSize,
        outline: textStyle?.outline ?? DEFAULT_TEXT_STYLE.outline,
        background: textStyle?.background ?? DEFAULT_TEXT_STYLE.background,
        textAnimation: textStyle?.textAnimation ?? DEFAULT_TEXT_STYLE.textAnimation,
        borderRadius: textStyle?.borderRadius ?? DEFAULT_TEXT_STYLE.borderRadius,
        captionCase: textStyle?.captionCase ?? DEFAULT_TEXT_STYLE.captionCase,
        hidePunctuation: textStyle?.hidePunctuation ?? DEFAULT_TEXT_STYLE.hidePunctuation,
        containerPadding: textStyle?.containerPadding ?? DEFAULT_TEXT_STYLE.containerPadding,
        boxPadding: textStyle?.boxPadding ?? DEFAULT_TEXT_STYLE.boxPadding
    };
    
    return (
        <div style={finalContainerStyles} className={className}>
            <AdvancedTextEngine
                page={pageData}
                config={textStyleConfig}
                videoConfig={{ fps: fps, width: videoConfig?.width || 1920, height: videoConfig?.height || 1080 }}
                currentFrame={currentFrame}
                position={getPositionHint(caption.position, caption.gridArea)}
            />
        </div>
    );
};

/**
 * Helper function to determine position hint for AdvancedTextEngine
 */
function getPositionHint(position?: Position, gridArea?: GridArea): 'center' | 'top' | 'bottom' {
    if (position) {
        if (position === Position.TopLeft || position === Position.TopCenter || position === Position.TopRight) {
            return 'top';
        }
        if (position === Position.BottomLeft || position === Position.BottomCenter || position === Position.BottomRight) {
            return 'bottom';
        }
    }
    
    if (gridArea) {
        // Map grid positions to hints
        if (gridArea.row === 1) return 'top';
        if (gridArea.row >= 4) return 'bottom';
    }
    
    return 'center'; // Default
}

/**
 * Generate default tokens for simple text when no token timing is provided
 */
function generateDefaultTokens(text: string, startAt: number, duration: number): CaptionToken[] {
    const words = text.split(' ');
    const startMs = startAt * 1000;
    const durationMs = duration * 1000;
    const wordDuration = durationMs / words.length;
    
    return words.map((word, index) => ({
        text: word,
        fromMs: startMs + (index * wordDuration),
        toMs: startMs + ((index + 1) * wordDuration),
        index: index
    }));
}

// Export the AdvancedCaptionContent component for external use
export { AdvancedCaptionContent };
